<template>
  <component-wrapper class="product-family-page-fi">
    <loading-indicator
      v-if="isLoading"
      class="product-family-page-fi__loading"
      variant="spinnerLocal"
    />

    <template v-else>
      <grid-container-fi>
        <div class="fi-col-span-12 fi-col-start-1">
          <product-family-head-with-facets-fi
            v-if="fields.enableHeaderFacets?.value"
            :catalog-data="catalogDataById"
            :media-download-link="mediaDownloadLink || undefined"
            @scroll-to-variants="scrollToVariants"
            @change-facet="changeFacet"
            @clear-facets="clearFacets"
          />

          <template v-else>
            <product-family-head-fi
              :catalog-data="catalogDataById"
              :media-download-link="mediaDownloadLink || undefined"
              :fields="fields || undefined"
              @scroll-to-variants="scrollToVariants"
            />

            <section-divider-small-fi />
          </template>

          <product-family-fi
            ref="variantsRef"
            :fields="fields"
            :family-line-drawing="familyLineDrawing?.Url"
            :max-headings="productsMaxHeadings"
            :catalog-data="catalogDataById"
            :approvals="approvals"
            class="product-family-page-fi__variants fi-mt-24 md:fi-mt-42 lg:fi-mt-72"
            @scroll-to-variants="scrollToVariants"
          />

          <section-divider-small-fi />

          <product-family-accordions-fi :catalog-data="catalogDataById" />
        </div>
      </grid-container-fi>

      <section-divider-small-fi />

      <product-items-slider-fi
        v-if="systemComponents?.length"
        :product-items="systemComponents"
        :slider-headline="$dict.get('product-Family.System-Components.Headline')"
        is-family
      />

      <product-items-slider-fi
        v-if="relatedCategories?.length"
        :product-items="relatedCategories"
        :slider-headline="$dict.get('product-Family.Related-Categories.Headline')"
        is-family
        is-light
      />

      <product-family-schema-fi
        :variants="searchProductsSchemaForFamilyNg?.hits"
        :category-data="catalogDataById"
      />
    </template>
  </component-wrapper>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { LoadingIndicator } from 'atoms';
import { computed, onMounted, onServerPrefetch, ref, watch } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import ProductFamilyAccordionsFi from '@/components/product-family/product-family-accordions-fi/ProductFamilyAccordionsFi.vue';
import ProductFamilyHeadFi from '@/components/product-family/product-family-head-fi/ProductFamilyHeadFi.vue';
import ProductFamilyHeadWithFacetsFi from '@/components/product-family/product-family-head-with-facets-fi/ProductFamilyHeadWithFacetsFi.vue';
import ProductFamilySchemaFi from '@/components/product-family/product-family-schema-fi/ProductFamilySchemaFi.vue';
import ProductItemsSliderFi from '@/components/product-items-slider-fi/ProductItemsSliderFi.vue';
import { useAppEvent } from '@/composables/appEvent';
import { useCatalog } from '@/composables/catalog';
import { useCatalogTextList } from '@/composables/catalogTextList';
import { useEventHub } from '@/composables/eventHub';
import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';
import { useSalesChannelFilter } from '@/composables/salesChannelFilter';
import { useTrackViewItem } from '@/composables/trackViewItem';
import type {
  CatalogCategoryData,
  CatalogSeriesData,
  SearchProductsForFamilyNgQuery,
  SearchProductsForFamilyNgQueryVariables,
  TextListItem,
} from '@/generated/graphql';
import GET_CATALOG_CATEGORY_DATA_BY_ID from '@/graphql/getCatalogCategoryDataById.gql';
import GET_CATALOG_SERIES_DATA_BY_ID from '@/graphql/getCatalogSeriesDataById.gql';
import SEARCH_PRODUCTS_SCHEMA_DATA_FOR_FAMILY_NG from '@/graphql/searchProductsSchemaDataForFamilyNg.gql';
import ProductFamilyFi from '@/pages/product-family/ProductFamilyFi.vue';
import SectionDividerSmallFi from '@/pages/section-divider-fi/SectionDividerSmallFi.vue';
import { useProductListStore } from '@/store/pinia/product-list';
import { scrollToElement } from '@/utils/smoothScroll';

const worstCaseMaxHeadings = 6;

const fields = useFields('ProductFamilyPageFiFields');

const productListStore = useProductListStore();
const fischer = useFischer();
const { withSalesChannelFilter, supplier, isPublicAssortment } = useSalesChannelFilter();
const { checkForMediaDownload, mediaDownloadLink } = useCatalog();
const { sendEvent } = useEventHub();
const { trackViewItem } = useTrackViewItem();
const { sendAppEvent } = useAppEvent();
const { textListValue } = useCatalogTextList();

const variantsRef = ref<InstanceType<typeof ProductFamilyFi> | null>(null);
const approvals = ref(['ZULASSUNG_D', 'ZULASSUNG_E', 'ZULASSUNG_I', 'SEISMIC']);
const catalogDataById = ref<CatalogCategoryData | CatalogSeriesData>();

const catalogId = computed(() => fischer.catalogId('1001340242'));

const marketingClaim = computed(() => {
  if (catalogDataById.value?.TextList) {
    return (
      textListValue(catalogDataById.value.TextList as TextListItem[], 'KAT_POS') ||
      textListValue(catalogDataById.value.TextList as TextListItem[], 'DOKBSTMARKCLAIM')
    );
  }
  return null;
});

const familyName = computed(() => {
  if (catalogDataById.value?.TextList) {
    return (
      textListValue(catalogDataById.value.TextList as TextListItem[], 'BEZEICHNUNG') ||
      textListValue(catalogDataById.value.TextList as TextListItem[], 'ARTIKELLANG')
    );
  }
  return null;
});

const catalogQuery = fischer.isProductSeriesEnabled()
  ? GET_CATALOG_SERIES_DATA_BY_ID
  : GET_CATALOG_CATEGORY_DATA_BY_ID;

// R1: catalog category/series query — SEO-critical content, needs R0
const {
  data: catalogQueryData,
  fetching: isLoading,
  error: catalogError,
  executeQuery: executeCatalogQuery,
} = useQuery({
  query: catalogQuery,
  variables: computed(() => ({
    baseSiteId: fischer.baseSiteId(),
    apiLang: fischer.catalogLocale(),
    categoryId: catalogId.value ?? '',
    seriesId: catalogId.value ?? '',
    includeLogisticAttributes: fischer.shop.isClosedShop(),
  })),
  pause: computed(() => !catalogId.value),
});

// immediate + flush:'sync' - see createSsrExchange. immediate: a restored SSR result is
// delivered during setup, so a lazy watcher never sees it. sync: Vue drops non-sync watchers
// during SSR entirely, so without it this state is never filled on the server.
// immediate: restored SSR results are delivered synchronously during setup, so a lazy watcher
// would never see them and catalogDataById would stay empty after hydration.
watch(
  catalogQueryData,
  (data) => {
    const result = data?.getCatalogCategoryDataById || data?.getCatalogSeriesDataById;
    if (result) {
      catalogDataById.value = result;
      trackViewItem(result, true);
      sendAppEvent('set-title', { title: familyName.value, subtitle: marketingClaim.value });
    }
  },
  { immediate: true, flush: 'sync' },
);

watch(catalogError, (error) => {
  if (error) {
    console.error(error);
  }
});

// R0: family/category data is SEO-critical, must be available on first SSR paint
onServerPrefetch(async () => {
  if (!catalogId.value) return;
  await executeCatalogQuery();
});

// R1: search products schema query — SEO-critical (JSON-LD schema data), needs R0
const {
  data: searchProductsResult,
  error: searchProductsError,
  executeQuery: executeSearchProductsQuery,
} = useQuery<SearchProductsForFamilyNgQuery, SearchProductsForFamilyNgQueryVariables>({
  query: SEARCH_PRODUCTS_SCHEMA_DATA_FOR_FAMILY_NG,
  variables: computed(() => ({
    id: catalogId.value ?? '',
    baseSiteId: fischer.baseSiteId(),
    factFinderChannel: fischer.factFinderChannel(),
    language: fischer.catalogLocale(),
    catalogLocale: fischer.catalogLocale(),
    page: 1,
    quantity: 10000,
    filters: [],
    extraFilters: withSalesChannelFilter([
      {
        associatedFieldName: 'Hidden_K',
        name: '!true',
      },
    ]),
    supplier: supplier(),
    publicAssortment: isPublicAssortment(),
  })),
  pause: computed(() => !fischer.baseSiteId() || !catalogId.value),
});

const searchProductsSchemaForFamilyNg = computed(
  () => searchProductsResult.value?.searchProductsForFamilyNg ?? null,
);

watch(searchProductsError, (error) => {
  if (error) {
    console.error(error);
  }
});

// R0: schema data for family page is SEO-critical (JSON-LD), must be on first SSR paint
onServerPrefetch(async () => {
  if (!fischer.baseSiteId() || !catalogId.value) return;
  await executeSearchProductsQuery();
});

const productsMaxHeadings = computed(() =>
  Math.max(
    (catalogDataById.value as CatalogCategoryData)?.categoryAttributes?.length ||
      (catalogDataById.value as CatalogSeriesData)?.seriesAttributes?.length ||
      0,
    worstCaseMaxHeadings,
  ),
);

const systemComponents = computed(() =>
  (catalogDataById.value as CatalogCategoryData)?.SystemComponents?.map(
    (component: string | null) => ({
      ProductId: component,
    }),
  ),
);

const relatedCategories = computed(() =>
  (catalogDataById.value as CatalogCategoryData)?.RelatedCategories?.map((category: string) => ({
    ProductId: category,
  })),
);

const lineDrawingConventions = [
  'PF_EB_F',
  'PF_EB_U',
  'PF_S_01_F',
  'PF_S_01_U',
  'PF_S_02_F',
  'PF_S_02_U',
];
const familyLineDrawing = computed(() => {
  return catalogDataById.value?.Media?.find(
    (media: any) =>
      media?.OutputType === 'PR' && lineDrawingConventions.includes(media.NameConvention || ''),
  );
});

// immediate + flush:'sync' - see createSsrExchange. immediate: the value is read during the child's
// setup. sync: `productsMaxHeadings` is 0 until the catalog query resolves in onServerPrefetch, and
// Vue drops non-sync watchers during SSR, so a pre-flush watcher leaves the store on its
// worst-case fallback for the whole server render. ProductListViewFi puts that number in its own
// query variables, so the server would fetch and serialise its list under a different urql
// operation key than the client asks for: guaranteed cache miss, an empty first client render, and
// a hydration mismatch across the whole variant list.
watch(
  productsMaxHeadings,
  (newValue) => {
    productListStore.setMaxHeadings(newValue);
  },
  { immediate: true, flush: 'sync' },
);

const changeFacet = (changes: any) => {
  variantsRef.value?.changeFacet(changes);
};

const clearFacets = () => {
  variantsRef.value?.clearFacets();
};

const scrollToVariants = () => {
  if (!variantsRef.value?.$el || !scrollToElement) return;

  scrollToElement(variantsRef.value.$el);
};

const handleEventTracking = () => {
  if (IS_SSR) {
    return;
  }

  try {
    const recommendationClickDetails = localStorage.getItem('recommendationClick');
    if (recommendationClickDetails) {
      const { refererId, itemQuantity, guided } = JSON.parse(recommendationClickDetails);
      sendEvent(guided ? 'guided_recommendation_click' : 'recommendation_click', {
        url: window ? window.location.href : '',
        productId: catalogId.value,
        refererId,
        itemQuantity,
      });

      localStorage.removeItem('recommendationClick');
    }
  } catch (error) {
    console.debug('failed to process recommendation click', error);
  }
};

onMounted(() => {
  if (catalogId.value) {
    const type = fischer.isProductSeriesEnabled() ? 'series' : 'category';

    checkForMediaDownload({
      type,
      id: catalogId.value,
    });
  }

  handleEventTracking();
});
</script>

<style scoped src="./product-family-page-fi" />
